/**
 * APIManagerService.ts - API Manager Service with Orchestrator Integration
 * 
 * CHANGE HISTORY:
 * - 251102_1619 danielbeer: Create service with Actr, Authr, Aggregatr, Configgr integration
 * 
 * Wraps APIManager with OpCentral orchestrators for:
 * - Actr: Action orchestration, CRUD operations
 * - Authr: Secure credential management (tokens, API keys)
 * - Aggregatr: Collections, history, persistence
 * - Configgr: Settings management per app
 */

import type { APIRequest, APIResponse, APICollection } from './APIManager';

export interface APIManagerConfig {
  appId: string;                    // App identifier for settings/storage
  defaultBaseUrl?: string;          // Default base URL for requests
  enableHistory?: boolean;          // Track request history (default: true)
  maxHistoryItems?: number;         // Max history items (default: 100)
  enableCollections?: boolean;      // Enable collections (default: true)
  autoSaveRequests?: boolean;       // Auto-save requests (default: false)
}

export interface APICredential {
  id: string;
  name: string;
  type: 'bearer' | 'basic' | 'api-key' | 'oauth2';
  value: string | { username: string; password: string } | { key: string; value: string };
  createdAt: string;
  updatedAt: string;
}

export interface APIEnvironment {
  id: string;
  name: string;
  variables: Record<string, string>;
  baseUrl?: string;
}

export class APIManagerService {
  private actr: any;
  private authr: any;
  private aggregatr: any;
  private configgr: any;
  private config: APIManagerConfig;
  private currentEnvironment: APIEnvironment | null = null;

  constructor(options: {
    actr: any;
    authr?: any;
    aggregatr?: any;
    configgr?: any;
    config: APIManagerConfig;
  }) {
    this.actr = options.actr;
    this.authr = options.authr;
    this.aggregatr = options.aggregatr;
    this.configgr = options.configgr;
    this.config = options.config;

    this._registerActions();
  }

  /**
   * Register Actr actions for API Manager operations
   */
  private _registerActions() {
    // Request CRUD
    this.actr.register('api-manager:create-request', {
      handler: this._createRequestAction.bind(this),
      description: 'Create API request',
      permissions: ['api:write']
    });
    this.actr.register('api-manager:update-request', {
      handler: this._updateRequestAction.bind(this),
      description: 'Update API request',
      permissions: ['api:write']
    });
    this.actr.register('api-manager:delete-request', {
      handler: this._deleteRequestAction.bind(this),
      description: 'Delete API request',
      permissions: ['api:delete']
    });
    this.actr.register('api-manager:execute-request', {
      handler: this._executeRequestAction.bind(this),
      description: 'Execute API request',
      permissions: ['api:execute']
    });

    // Collection CRUD
    this.actr.register('api-manager:create-collection', {
      handler: this._createCollectionAction.bind(this),
      description: 'Create API collection',
      permissions: ['api:write']
    });
    this.actr.register('api-manager:update-collection', {
      handler: this._updateCollectionAction.bind(this),
      description: 'Update API collection',
      permissions: ['api:write']
    });
    this.actr.register('api-manager:delete-collection', {
      handler: this._deleteCollectionAction.bind(this),
      description: 'Delete API collection',
      permissions: ['api:delete']
    });

    // Credential management (via Authr)
    this.actr.register('api-manager:save-credential', {
      handler: this._saveCredentialAction.bind(this),
      description: 'Save API credential',
      permissions: ['credential:write']
    });
    this.actr.register('api-manager:get-credential', {
      handler: this._getCredentialAction.bind(this),
      description: 'Get API credential',
      permissions: ['credential:read']
    });
    this.actr.register('api-manager:delete-credential', {
      handler: this._deleteCredentialAction.bind(this),
      description: 'Delete API credential',
      permissions: ['credential:delete']
    });

    // Environment management
    this.actr.register('api-manager:create-environment', {
      handler: this._createEnvironmentAction.bind(this),
      description: 'Create environment',
      permissions: ['environment:write']
    });
    this.actr.register('api-manager:switch-environment', {
      handler: this._switchEnvironmentAction.bind(this),
      description: 'Switch environment',
      permissions: ['environment:read']
    });

    // History
    this.actr.register('api-manager:add-to-history', {
      handler: this._addToHistoryAction.bind(this),
      description: 'Add to request history',
      permissions: ['history:write']
    });
    this.actr.register('api-manager:get-history', {
      handler: this._getHistoryAction.bind(this),
      description: 'Get request history',
      permissions: ['history:read']
    });
    this.actr.register('api-manager:clear-history', {
      handler: this._clearHistoryAction.bind(this),
      description: 'Clear request history',
      permissions: ['history:delete']
    });
  }

  // ============================================================================
  // PUBLIC API (via Actr)
  // ============================================================================

  /**
   * Create a new API request
   */
  async createRequest(request: Omit<APIRequest, 'id'>): Promise<APIRequest> {
    return await this.actr.execute('api-manager:create-request', { request });
  }

  /**
   * Update an existing request
   */
  async updateRequest(id: string, updates: Partial<APIRequest>): Promise<APIRequest> {
    return await this.actr.execute('api-manager:update-request', { id, updates });
  }

  /**
   * Delete a request
   */
  async deleteRequest(id: string): Promise<void> {
    await this.actr.execute('api-manager:delete-request', { id });
  }

  /**
   * Execute an API request
   */
  async executeRequest(request: APIRequest): Promise<APIResponse> {
    return await this.actr.execute('api-manager:execute-request', { request });
  }

  /**
   * Create a collection
   */
  async createCollection(name: string): Promise<APICollection> {
    return await this.actr.execute('api-manager:create-collection', { name });
  }

  /**
   * Update a collection
   */
  async updateCollection(id: string, updates: Partial<APICollection>): Promise<APICollection> {
    return await this.actr.execute('api-manager:update-collection', { id, updates });
  }

  /**
   * Delete a collection
   */
  async deleteCollection(id: string): Promise<void> {
    await this.actr.execute('api-manager:delete-collection', { id });
  }

  /**
   * Get all collections
   */
  async getCollections(): Promise<APICollection[]> {
    if (!this.aggregatr) {
      return [];
    }

    const result = await this.aggregatr.fetch({
      source: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:collections`
      }
    });

    return result?.data || [];
  }

  /**
   * Save credential securely (via Authr)
   */
  async saveCredential(credential: Omit<APICredential, 'id' | 'createdAt' | 'updatedAt'>): Promise<APICredential> {
    return await this.actr.execute('api-manager:save-credential', { credential });
  }

  /**
   * Get credential securely (via Authr)
   */
  async getCredential(id: string): Promise<APICredential | null> {
    return await this.actr.execute('api-manager:get-credential', { id });
  }

  /**
   * Delete credential
   */
  async deleteCredential(id: string): Promise<void> {
    await this.actr.execute('api-manager:delete-credential', { id });
  }

  /**
   * List all credentials (metadata only, no values)
   */
  async listCredentials(): Promise<Array<Omit<APICredential, 'value'>>> {
    if (!this.authr) {
      return [];
    }

    // Get credential metadata from Authr
    const credentials = await this.authr.listCredentials({
      scope: `api-manager:${this.config.appId}`
    });

    return credentials;
  }

  /**
   * Create environment
   */
  async createEnvironment(environment: Omit<APIEnvironment, 'id'>): Promise<APIEnvironment> {
    return await this.actr.execute('api-manager:create-environment', { environment });
  }

  /**
   * Switch active environment
   */
  async switchEnvironment(id: string): Promise<void> {
    await this.actr.execute('api-manager:switch-environment', { id });
  }

  /**
   * Get current environment
   */
  getCurrentEnvironment(): APIEnvironment | null {
    return this.currentEnvironment;
  }

  /**
   * Get request history
   */
  async getHistory(): Promise<APIRequest[]> {
    return await this.actr.execute('api-manager:get-history', {});
  }

  /**
   * Clear history
   */
  async clearHistory(): Promise<void> {
    await this.actr.execute('api-manager:clear-history', {});
  }

  /**
   * Get settings for this app
   */
  async getSettings(): Promise<any> {
    if (!this.configgr) {
      return {};
    }

    return await this.configgr.get(`api-manager:${this.config.appId}`);
  }

  /**
   * Update settings
   */
  async updateSettings(settings: any): Promise<void> {
    if (!this.configgr) {
      return;
    }

    await this.configgr.set(`api-manager:${this.config.appId}`, settings);
  }

  // ============================================================================
  // PRIVATE ACTION HANDLERS
  // ============================================================================

  private async _createRequestAction({ request }: { request: Omit<APIRequest, 'id'> }): Promise<APIRequest> {
    const newRequest: APIRequest = {
      ...request,
      id: this._generateId()
    };

    // Save to Aggregatr if available
    if (this.aggregatr && this.config.enableCollections) {
      await this.aggregatr.save({
        destination: {
          type: 'local-storage',
          key: `api-manager:${this.config.appId}:request:${newRequest.id}`
        },
        data: newRequest
      });
    }

    return newRequest;
  }

  private async _updateRequestAction({ id, updates }: { id: string; updates: Partial<APIRequest> }): Promise<APIRequest> {
    if (!this.aggregatr) {
      throw new Error('Aggregatr not available');
    }

    // Fetch existing
    const existing = await this.aggregatr.fetch({
      source: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:request:${id}`
      }
    });

    const updated = { ...existing.data, ...updates };

    // Save updated
    await this.aggregatr.save({
      destination: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:request:${id}`
      },
      data: updated
    });

    return updated;
  }

  private async _deleteRequestAction({ id }: { id: string }): Promise<void> {
    if (!this.aggregatr) {
      return;
    }

    // Delete from storage
    await this.aggregatr.delete({
      source: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:request:${id}`
      }
    });
  }

  private async _executeRequestAction({ request }: { request: APIRequest }): Promise<APIResponse> {
    const startTime = Date.now();

    try {
      // Resolve environment variables
      const resolvedRequest = this._resolveEnvironmentVariables(request);

      // Get auth from Authr if credential ID is provided
      if (request.auth?.type && this.authr) {
        const credentialId = (request.auth as any).credentialId;
        if (credentialId) {
          const credential = await this.getCredential(credentialId);
          if (credential) {
            resolvedRequest.auth = this._credentialToAuth(credential);
          }
        }
      }

      // Build fetch options
      const fetchOptions: RequestInit = {
        method: resolvedRequest.method,
        headers: this._buildHeaders(resolvedRequest)
      };

      // Add body if applicable
      if (resolvedRequest.body && ['POST', 'PUT', 'PATCH'].includes(resolvedRequest.method)) {
        fetchOptions.body = resolvedRequest.body;
      }

      // Build URL with params
      const url = this._buildUrl(resolvedRequest);

      // Execute request
      const response = await fetch(url, fetchOptions);

      // Parse response
      const body = await response.text();
      const endTime = Date.now();

      const apiResponse: APIResponse = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body,
        time: endTime - startTime,
        size: new Blob([body]).size
      };

      // Add to history
      if (this.config.enableHistory) {
        await this.actr.execute('api-manager:add-to-history', { request: resolvedRequest });
      }

      return apiResponse;
    } catch (error: any) {
      const endTime = Date.now();
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: error.message,
        time: endTime - startTime,
        size: 0
      };
    }
  }

  private async _createCollectionAction({ name }: { name: string }): Promise<APICollection> {
    const collection: APICollection = {
      id: this._generateId(),
      name,
      requests: []
    };

    if (this.aggregatr) {
      // Get existing collections
      const collections = await this.getCollections();
      collections.push(collection);

      // Save updated list
      await this.aggregatr.save({
        destination: {
          type: 'local-storage',
          key: `api-manager:${this.config.appId}:collections`
        },
        data: collections
      });
    }

    return collection;
  }

  private async _updateCollectionAction({ id, updates }: { id: string; updates: Partial<APICollection> }): Promise<APICollection> {
    if (!this.aggregatr) {
      throw new Error('Aggregatr not available');
    }

    const collections = await this.getCollections();
    const index = collections.findIndex(c => c.id === id);
    
    if (index === -1) {
      throw new Error('Collection not found');
    }

    collections[index] = { ...collections[index], ...updates };

    await this.aggregatr.save({
      destination: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:collections`
      },
      data: collections
    });

    return collections[index];
  }

  private async _deleteCollectionAction({ id }: { id: string }): Promise<void> {
    if (!this.aggregatr) {
      return;
    }

    const collections = await this.getCollections();
    const filtered = collections.filter(c => c.id !== id);

    await this.aggregatr.save({
      destination: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:collections`
      },
      data: filtered
    });
  }

  private async _saveCredentialAction({ credential }: { credential: Omit<APICredential, 'id' | 'createdAt' | 'updatedAt'> }): Promise<APICredential> {
    if (!this.authr) {
      throw new Error('Authr not available');
    }

    const id = this._generateId();
    const now = new Date().toISOString();

    const fullCredential: APICredential = {
      ...credential,
      id,
      createdAt: now,
      updatedAt: now
    };

    // Store securely in Authr
    await this.authr.storeCredential({
      id,
      scope: `api-manager:${this.config.appId}`,
      name: credential.name,
      type: credential.type,
      value: credential.value,
      metadata: {
        createdAt: now,
        updatedAt: now
      }
    });

    return fullCredential;
  }

  private async _getCredentialAction({ id }: { id: string }): Promise<APICredential | null> {
    if (!this.authr) {
      return null;
    }

    const credential = await this.authr.getCredential({
      id,
      scope: `api-manager:${this.config.appId}`
    });

    if (!credential) {
      return null;
    }

    return {
      id: credential.id,
      name: credential.name,
      type: credential.type,
      value: credential.value,
      createdAt: credential.metadata?.createdAt || '',
      updatedAt: credential.metadata?.updatedAt || ''
    };
  }

  private async _deleteCredentialAction({ id }: { id: string }): Promise<void> {
    if (!this.authr) {
      return;
    }

    await this.authr.deleteCredential({
      id,
      scope: `api-manager:${this.config.appId}`
    });
  }

  private async _createEnvironmentAction({ environment }: { environment: Omit<APIEnvironment, 'id'> }): Promise<APIEnvironment> {
    const env: APIEnvironment = {
      ...environment,
      id: this._generateId()
    };

    if (this.aggregatr) {
      await this.aggregatr.save({
        destination: {
          type: 'local-storage',
          key: `api-manager:${this.config.appId}:environment:${env.id}`
        },
        data: env
      });
    }

    return env;
  }

  private async _switchEnvironmentAction({ id }: { id: string }): Promise<void> {
    if (!this.aggregatr) {
      return;
    }

    const env = await this.aggregatr.fetch({
      source: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:environment:${id}`
      }
    });

    this.currentEnvironment = env.data;

    // Save current environment preference
    if (this.configgr) {
      await this.configgr.set(`api-manager:${this.config.appId}:current-environment`, id);
    }
  }

  private async _addToHistoryAction({ request }: { request: APIRequest }): Promise<void> {
    if (!this.aggregatr || !this.config.enableHistory) {
      return;
    }

    const history = await this.getHistory();
    history.unshift(request);

    // Limit history size
    const maxItems = this.config.maxHistoryItems || 100;
    if (history.length > maxItems) {
      history.splice(maxItems);
    }

    await this.aggregatr.save({
      destination: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:history`
      },
      data: history
    });
  }

  private async _getHistoryAction(): Promise<APIRequest[]> {
    if (!this.aggregatr) {
      return [];
    }

    const result = await this.aggregatr.fetch({
      source: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:history`
      }
    });

    return result?.data || [];
  }

  private async _clearHistoryAction(): Promise<void> {
    if (!this.aggregatr) {
      return;
    }

    await this.aggregatr.save({
      destination: {
        type: 'local-storage',
        key: `api-manager:${this.config.appId}:history`
      },
      data: []
    });
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private _generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private _resolveEnvironmentVariables(request: APIRequest): APIRequest {
    if (!this.currentEnvironment) {
      return request;
    }

    const resolved = { ...request };
    const vars = this.currentEnvironment.variables;

    // Replace {{variable}} in URL
    resolved.url = this._replaceVariables(request.url, vars);

    // Replace in headers
    resolved.headers = Object.fromEntries(
      Object.entries(request.headers).map(([k, v]) => [
        k,
        this._replaceVariables(v, vars)
      ])
    );

    // Replace in body
    if (request.body) {
      resolved.body = this._replaceVariables(request.body, vars);
    }

    return resolved;
  }

  private _replaceVariables(text: string, vars: Record<string, string>): string {
    let result = text;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
    }
    return result;
  }

  private _buildHeaders(request: APIRequest): Record<string, string> {
    const headers = { ...request.headers };

    // Add auth headers
    if (request.auth) {
      if (request.auth.type === 'bearer' && request.auth.token) {
        headers['Authorization'] = `Bearer ${request.auth.token}`;
      } else if (request.auth.type === 'basic' && request.auth.username && request.auth.password) {
        const encoded = btoa(`${request.auth.username}:${request.auth.password}`);
        headers['Authorization'] = `Basic ${encoded}`;
      } else if (request.auth.type === 'api-key' && request.auth.key && request.auth.value) {
        headers[request.auth.key] = request.auth.value;
      }
    }

    // Add content-type for body
    if (request.body && !headers['Content-Type']) {
      if (request.bodyType === 'json') {
        headers['Content-Type'] = 'application/json';
      } else if (request.bodyType === 'form') {
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
    }

    return headers;
  }

  private _buildUrl(request: APIRequest): string {
    let url = request.url;

    // Add base URL from environment if relative
    if (this.currentEnvironment?.baseUrl && !url.startsWith('http')) {
      url = this.currentEnvironment.baseUrl + url;
    }

    // Add query params
    if (Object.keys(request.params).length > 0) {
      const params = new URLSearchParams(request.params);
      url += (url.includes('?') ? '&' : '?') + params.toString();
    }

    return url;
  }

  private _credentialToAuth(credential: APICredential): APIRequest['auth'] {
    if (credential.type === 'bearer') {
      return {
        type: 'bearer',
        token: credential.value as string
      };
    } else if (credential.type === 'basic') {
      const { username, password } = credential.value as { username: string; password: string };
      return {
        type: 'basic',
        username,
        password
      };
    } else if (credential.type === 'api-key') {
      const { key, value } = credential.value as { key: string; value: string };
      return {
        type: 'api-key',
        key,
        value
      };
    }
    return { type: 'none' };
  }
}

export default APIManagerService;
